import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { 
  TrendingUp, TrendingDown, Activity, FileText, Download, 
  Presentation, RefreshCw, Clock, CheckCircle, AlertTriangle,
  BarChart3, Shield, Target, FileSpreadsheet, Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Reports = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Export state
  const [reportTypes, setReportTypes] = useState([]);
  const [exportFormats, setExportFormats] = useState([]);
  const [reportHistory, setReportHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  
  // Export form state
  const [selectedReportType, setSelectedReportType] = useState('campaign_performance');
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [clientName, setClientName] = useState('MediaKshetra');
  const [useDemoData, setUseDemoData] = useState(true);

  useEffect(() => {
    fetchReports();
    fetchReportTypes();
    fetchReportHistory();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/overview`, {
        withCredentials: true
      });
      setOverview(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportTypes = async () => {
    try {
      const response = await fetch(`${API}/gios/reports/types`);
      if (response.ok) {
        const data = await response.json();
        setReportTypes(data.report_types || []);
        setExportFormats(data.export_formats || []);
      }
    } catch (error) {
      console.error('Error fetching report types:', error);
    }
  };

  const fetchReportHistory = async () => {
    try {
      const response = await fetch(`${API}/gios/reports/history`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReportHistory(data || []);
      }
    } catch (error) {
      console.error('Error fetching report history:', error);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/gios/reports/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_type: selectedReportType,
          export_format: selectedFormat,
          client_name: clientName,
          use_demo_data: useDemoData
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Decode base64 and trigger download
        const byteCharacters = atob(data.content_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.content_type });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.file_name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success(`Report generated: ${data.file_name}`);
        fetchReportHistory();
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const getReportIcon = (reportType) => {
    switch (reportType) {
      case 'campaign_performance': return <BarChart3 className="w-5 h-5" />;
      case 'governance_compliance': return <Shield className="w-5 h-5" />;
      case 'moi_opportunity': return <Target className="w-5 h-5" />;
      case 'executive_summary': return <FileSpreadsheet className="w-5 h-5" />;
      case 'taxonomy_audit': return <FileText className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading reports...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">Reports & Analytics</h1>
                <p className="text-sm text-muted-foreground">
                  Performance insights and export center
                </p>
              </div>
            </div>
          </div>
          <Badge variant="outline" className="text-xs px-3 py-1 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
            GIOS v2.0
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-800/50 border border-zinc-700">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Activity className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="export" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Download className="w-4 h-4 mr-2" />
              Export Center
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Clock className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="mt-6">
            {/* Key Metrics */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Total ROI</span>
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="text-3xl font-bold mb-1">342%</div>
                  <div className="text-sm text-green-500">+23% from last month</div>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Cost Per Conversion</span>
                    <TrendingDown className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="text-3xl font-bold mb-1">$12.43</div>
                  <div className="text-sm text-green-500">-8% from last month</div>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Conversion Rate</span>
                    <Activity className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-3xl font-bold mb-1">4.8%</div>
                  <div className="text-sm text-muted-foreground">Industry avg: 3.2%</div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle>Performance Trend</CardTitle>
                  <CardDescription>Last 7 days</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={overview?.trend_data || [
                      { name: 'Mon', value: 4000 },
                      { name: 'Tue', value: 3000 },
                      { name: 'Wed', value: 5000 },
                      { name: 'Thu', value: 4500 },
                      { name: 'Fri', value: 6000 },
                      { name: 'Sat', value: 5500 },
                      { name: 'Sun', value: 7000 },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="name" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                      <Line type="monotone" dataKey="value" stroke="#D4A84B" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle>Platform Distribution</CardTitle>
                  <CardDescription>Spend allocation</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Google', value: 48 },
                          { name: 'Meta', value: 28 },
                          { name: 'TikTok', value: 9 },
                          { name: 'DV360', value: 15 },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}%`}
                      >
                        {COLORS.map((color, index) => (
                          <Cell key={`cell-${index}`} fill={color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Export Center Tab */}
          <TabsContent value="export" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Report Generator */}
              <div className="lg:col-span-2">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Download className="w-5 h-5 text-primary" />
                      Generate Report
                    </CardTitle>
                    <CardDescription>
                      Export professional reports in PDF or PowerPoint format
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Report Type Selection */}
                    <div className="space-y-3">
                      <Label>Report Type</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {reportTypes.map((type) => (
                          <div
                            key={type.value}
                            onClick={() => setSelectedReportType(type.value)}
                            className={`p-4 rounded-lg border cursor-pointer transition-all ${
                              selectedReportType === type.value
                                ? 'border-primary bg-primary/10'
                                : 'border-zinc-700 bg-zinc-800/30 hover:border-zinc-600'
                            }`}
                            data-testid={`report-type-${type.value}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-md ${
                                selectedReportType === type.value
                                  ? 'bg-primary/20 text-primary'
                                  : 'bg-zinc-700 text-zinc-400'
                              }`}>
                                {getReportIcon(type.value)}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-sm">{type.label}</h4>
                                <p className="text-xs text-zinc-500 mt-1">{type.description}</p>
                              </div>
                              {selectedReportType === type.value && (
                                <CheckCircle className="w-4 h-4 text-primary" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Export Format */}
                    <div className="space-y-3">
                      <Label>Export Format</Label>
                      <div className="flex gap-3">
                        {exportFormats.map((format) => (
                          <Button
                            key={format.value}
                            variant={selectedFormat === format.value ? 'default' : 'outline'}
                            onClick={() => setSelectedFormat(format.value)}
                            className="flex-1"
                            data-testid={`format-${format.value}`}
                          >
                            {format.value === 'pdf' ? (
                              <FileText className="w-4 h-4 mr-2" />
                            ) : (
                              <Presentation className="w-4 h-4 mr-2" />
                            )}
                            {format.label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Client Name */}
                    <div className="space-y-2">
                      <Label>Client/Report Name</Label>
                      <Input
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="Client or report name"
                        className="bg-zinc-800/50 border-zinc-700"
                      />
                    </div>

                    {/* Demo Data Toggle */}
                    <div className="flex items-center justify-between p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                        <div>
                          <p className="text-sm font-medium text-amber-300">Demo Data Mode</p>
                          <p className="text-xs text-amber-400/70">Uses pre-seeded sample data for demonstration</p>
                        </div>
                      </div>
                      <Button
                        variant={useDemoData ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setUseDemoData(!useDemoData)}
                        className={useDemoData ? 'bg-amber-500 hover:bg-amber-600' : ''}
                      >
                        {useDemoData ? 'Demo ON' : 'Demo OFF'}
                      </Button>
                    </div>

                    {/* Generate Button */}
                    <Button
                      onClick={handleGenerateReport}
                      disabled={generating}
                      className="w-full"
                      size="lg"
                      data-testid="generate-report-btn"
                    >
                      {generating ? (
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-5 h-5 mr-2" />
                      )}
                      {generating ? 'Generating...' : 'Generate & Download Report'}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Stats */}
              <div className="space-y-6">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Quick Export</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        setSelectedReportType('campaign_performance');
                        setSelectedFormat('pdf');
                        handleGenerateReport();
                      }}
                      disabled={generating}
                    >
                      <FileText className="w-4 h-4 mr-2 text-red-400" />
                      Campaign PDF
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        setSelectedReportType('campaign_performance');
                        setSelectedFormat('pptx');
                        handleGenerateReport();
                      }}
                      disabled={generating}
                    >
                      <Presentation className="w-4 h-4 mr-2 text-orange-400" />
                      Campaign PPTX
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        setSelectedReportType('moi_opportunity');
                        setSelectedFormat('pdf');
                        handleGenerateReport();
                      }}
                      disabled={generating}
                    >
                      <Target className="w-4 h-4 mr-2 text-emerald-400" />
                      MOI Analysis PDF
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        setSelectedReportType('governance_compliance');
                        setSelectedFormat('pdf');
                        handleGenerateReport();
                      }}
                      disabled={generating}
                    >
                      <Shield className="w-4 h-4 mr-2 text-amber-400" />
                      Governance PDF
                    </Button>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Report Info</CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs space-y-2 text-zinc-500">
                    <p>• PDF: Detailed document format</p>
                    <p>• PPTX: Presentation slides</p>
                    <p>• MediaKshetra branded templates</p>
                    <p>• Professional charts & tables</p>
                    <p>• Auto-generated insights</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Report History</CardTitle>
                    <CardDescription>Previously generated reports</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchReportHistory}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {reportHistory.length > 0 ? (
                  <div className="space-y-3">
                    {reportHistory.map((report, idx) => (
                      <div
                        key={report.report_id || idx}
                        className="p-4 rounded-lg bg-zinc-800/30 border border-zinc-700 hover:border-zinc-600 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-md bg-zinc-700">
                              {getReportIcon(report.report_type)}
                            </div>
                            <div>
                              <h4 className="font-medium text-sm">{report.file_name}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-[10px]">
                                  {report.export_format?.toUpperCase()}
                                </Badge>
                                <span className="text-[10px] text-zinc-500">
                                  {report.client_name}
                                </span>
                                <span className="text-[10px] text-zinc-500">
                                  {new Date(report.generated_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-zinc-500">
                              {(report.file_size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-zinc-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>No reports generated yet</p>
                    <p className="text-xs mt-1">Generate your first report using the Export Center</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Reports;
